import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import DataTable, { StatusPill } from './DataTable.jsx'
import { siteUrl, outreachOf } from '../lib/companyMeta.js'
import { isRowEnriched } from '../lib/enrichedLeads.js'
import { formatAmount, formatDay, formatDayTime, hubspotDate } from '../lib/hubspot.js'
import {
  usageForLead,
  usageEpoch,
  usageNumber,
  usersCountOf,
  usageBool,
  subscriptionStatus,
  SUBSCRIPTION_WARN_DAYS,
  usageDashReason,
  usageFieldRule,
  usageStringDay,
  usageStringDayEpoch,
} from '../lib/leadUsage.js'
import { intentScoreOf, intentTier, freshCalibration, warmIntentScores } from '../lib/intentScore.js'
import {
  BuildingIcon,
  MarketMapIcon,
  TagIcon,
  UserIcon,
  HashIcon,
  CalendarIcon,
  MailIcon,
  LeadQualityIcon,
} from './icons.jsx'

const PAGE_SIZE = 30
const EMPTY = new Set()

export const keyOf = (c) => c.company_id || c.profile_link || c.name

function formatPhone(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

function hostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function splitList(value) {
  return String(value || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
}

// How the mirror found the company, in a word — for the HubSpot badge tooltip.
// Mirrors the confidence values the data layer attaches (row._hubspot.confidence).
const HS_MATCH_BASIS = {
  domain: 'website domain',
  email: 'contact email',
  name: 'company name',
}

// Compact relative age for the HubSpot Activity column. Parses the same ISO /
// epoch strings the mirror emits (hubspotDate); empty when there's nothing to
// show, so the cell can fall back to an em dash rather than "Invalid Date".
function relativeAge(value) {
  const d = hubspotDate(value)
  if (!d) return ''
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`
  const days = Math.floor(mins / 1440)
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

// Selection + saved state are owned by the parent (CompanyDirectory) so the
// Save/Clear actions can live in the filter row next to Sync and the
// "N selected" count can live in the table footer.
export default function CompanyTable({
  companies,
  category,
  selectedKeys = EMPTY,
  onToggleRow,
  onToggleAll,
  enriched = EMPTY,
  enriching = EMPTY,
  columnsOpen,
  onColumnsOpenChange,
  // Callers can append their own columns and give the table its own
  // persisted column prefs / sort.
  extraColumns = [],
  tableId,
  defaultSort = { key: 'score', dir: 'desc' },
  // The rep's sent-folder index, so the Outreach column tells the same truth
  // the filter does: HubSpot's log UNION what he actually sent.
  sentMail = null,
  // The product-usage payload (lib/hubspot.js hubspotUsers), or null while it
  // loads / when it failed. LEAVE IT UNDEFINED (don't pass the prop) to omit
  // the usage columns entirely — a caller that never fetches usage (e.g. the
  // audience list) must not render a wall of dashes that reads as "nobody
  // uses the software". The directory always passes a value, so its columns
  // exist from the first frame and fill in when the read lands.
  usage,
  emptyMessage = 'No companies match your filters.',
  loading = false,
  loadingMessage,
}) {
  const navigate = useNavigate()
  // An audience list mixes both directories, so each row carries its own
  // `_listCategory`. Trust that over the table-level prop: the category ends up
  // in the lead page's nav state, where a wrong value makes enrichLead() miss
  // the cache and the lead read as never-researched.
  const catOf = (c) => c._listCategory || category
  // One calibration read per pass, and every score warmed in a single linear
  // pass before the sort runs. DataTable's comparator calls sortValue ~19x per
  // row (measured 711,584 calls for 37k rows), so warming first turns that into
  // one compute per row plus cheap Map hits.
  const calib = useMemo(() => freshCalibration(), [companies])
  useMemo(() => warmIntentScores(companies, calib), [companies, calib])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Company',
        icon: BuildingIcon,
        sortable: true,
        maxWidth: 260,
        render: (c) => (
          <span className="flex items-center gap-x-2">
            <span className="min-w-0 truncate font-semibold">{c.name}</span>
            {/* In flight beats done: a re-run should read as working, not stale. */}
            {enriching.has(keyOf(c)) ? (
              <span
                className="inline-flex flex-none items-center gap-x-1.5 whitespace-nowrap rounded-full bg-[#f5f3ff] px-[9px] py-[3px] text-[12px] font-semibold text-[#6d28d9] dark:bg-[rgba(109,40,217,0.22)] dark:text-[#c4b5fd]"
                title="AI research is running on this company"
              >
                <span className="h-[9px] w-[9px] animate-spin rounded-full [border:1.5px_solid_currentColor] [border-top-color:transparent]" />
                Enriching…
              </span>
            ) : (
              // Enriched here or enriched before it was imported — either way
              // the research exists, so the row must not read as untouched.
              isRowEnriched(c, enriched) && (
                <span
                  className="flex-none whitespace-nowrap rounded-full bg-[#f5f3ff] px-[9px] py-[3px] text-[12px] font-semibold text-[#6d28d9] dark:bg-[rgba(109,40,217,0.22)] dark:text-[#c4b5fd]"
                  title={
                    enriched.has(keyOf(c))
                      ? 'AI research has been run on this company'
                      : 'Researched in the imported dataset — open the lead to see it'
                  }
                >
                  Enriched
                </span>
              )
            )}
            {/* One brand with several offices — each is a separate lead with its
                own local number, so the repeated name is data, not a duplicate.
                The Location column says which office this row is. */}
            {Number(c.locations) > 1 && (
              <span
                className="flex-none whitespace-nowrap rounded-[6px] border border-line bg-panel px-[7px] py-[2px] text-[11.5px] text-dim"
                title={`${c.locations} offices share this name and website — this row is the ${c.city_state || 'listed'} one`}
              >
                {c.locations} locations
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'website',
        header: 'Website',
        icon: MarketMapIcon,
        sortable: true,
        maxWidth: 240,
        render: (c) =>
          c.website ? (
            <a
              className="text-[13px] text-primary-dark no-underline hover:underline"
              href={siteUrl(c.website)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {hostname(c.website)}
            </a>
          ) : (
            <span className="text-faint">N/A</span>
          ),
      },
      {
        key: 'intent',
        header: 'Intent',
        icon: LeadQualityIcon,
        sortable: true,
        // It's the table's default sort, so it has to be visible even for users
        // whose saved column prefs predate it.
        alwaysShow: true,
        align: 'center',
        // Sorted on the raw number; the chip is presentation only.
        sortValue: (c) => intentScoreOf(c, calib),
        render: (c) => {
          const score = intentScoreOf(c, calib)
          const tier = intentTier(score)
          return (
            <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
              {/* Same weight and size as every other cell value (Location is the
                  reference) — the number is data, not a heading. tabular-nums
                  only so the column stays aligned. */}
              <span className="tabular-nums text-ink">{score}</span>
              <span className={`rounded-[4px] px-[4px] py-px text-[9.5px] font-semibold tracking-[0.02em] ${tier.chip}`}>
                {tier.label}
              </span>
            </span>
          )
        },
      },
      {
        // The imported research score (0–10 from the dataset) — distinct from
        // the app-computed Intent score. Opt-in via the column organizer.
        key: 'score',
        header: 'Score',
        icon: LeadQualityIcon,
        sortable: true,
        align: 'center',
        // The table's default sort, so it must be visible even for users whose
        // saved column prefs predate it — same reasoning as Intent above.
        alwaysShow: true,
        sortValue: (c) => Number(c.score) || 0,
        render: (c) =>
          c.score != null && String(c.score).trim() !== '' ? (
            <span className="tabular-nums text-ink">{c.score}</span>
          ) : (
            <span className="text-faint">N/A</span>
          ),
      },
      {
        key: 'email',
        header: 'Email',
        icon: MailIcon,
        sortable: true,
        maxWidth: 260,
        render: (c) =>
          c.email ? (
            <a
              className="text-[13px] text-primary-dark no-underline hover:underline"
              href={`mailto:${c.email}`}
              onClick={(e) => e.stopPropagation()}
            >
              {c.email}
            </a>
          ) : (
            <span className="text-faint">N/A</span>
          ),
      },
      {
        key: 'city_state',
        header: 'Location',
        icon: MarketMapIcon,
        sortable: true,
        maxWidth: 220,
        render: (c) => c.city_state || <span className="text-faint">N/A</span>,
      },
      {
        key: 'trade',
        header: 'Industry',
        icon: TagIcon,
        sortable: true,
        render: (c) => {
          const trades = splitList(c.trade)
          if (trades.length === 0) return <span className="text-faint">N/A</span>
          return (
            <span className="inline-flex items-center gap-x-1 align-middle" title={trades.join(', ')}>
              {trades.slice(0, 2).map((t) => (
                <span className="whitespace-nowrap rounded-[6px] border border-line bg-panel px-[7px] py-[2px] text-[11.5px] text-dim" key={t}>
                  {t}
                </span>
              ))}
              {trades.length > 2 && <span className="text-[11.5px] font-semibold text-faint">+{trades.length - 2}</span>}
            </span>
          )
        },
      },
      {
        // Commercial / residential mix from the dataset — opt-in column.
        key: 'commercial_residential',
        header: 'Com / Res',
        pickerLabel: 'Commercial / Residential',
        icon: TagIcon,
        sortable: true,
        maxWidth: 200,
        defaultHidden: true,
        render: (c) => c.commercial_residential || <span className="text-faint">N/A</span>,
      },
      {
        key: 'service_area',
        header: 'Service Area',
        icon: MarketMapIcon,
        sortable: true,
        maxWidth: 220,
        render: (c) => c.service_area || <span className="text-faint">N/A</span>,
      },
      {
        key: 'established',
        header: 'Est.',
        icon: CalendarIcon,
        sortable: true,
        align: 'center',
        sortValue: (c) => Number(c.established) || 0,
        render: (c) => c.established || <span className="text-faint">N/A</span>,
      },
      {
        key: 'company_size',
        header: 'Size',
        icon: HashIcon,
        sortable: true,
        render: (c) => c.company_size || <span className="text-faint">N/A</span>,
      },
      {
        key: 'annual_volume',
        header: 'Annual Volume',
        icon: HashIcon,
        sortable: true,
        render: (c) =>
          !c.annual_volume || c.annual_volume === 'Undisclosed' ? (
            <span className="text-faint">Undisclosed</span>
          ) : (
            c.annual_volume
          ),
      },
      {
        key: 'contact_name',
        header: 'Primary Contact',
        icon: UserIcon,
        sortable: true,
        render: (c) =>
          c.contact_name ? (
            <span className="inline-flex items-center gap-x-2 whitespace-nowrap">
              <span className="font-semibold">{c.contact_name}</span>
              {/* Tappable: the rep works this table from a phone between calls,
                  so the number has to dial rather than just display. */}
              {c.contact_phone && (
                <a
                  href={`tel:${String(c.contact_phone).replace(/[^0-9+]/g, '')}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[12px] text-primary-dark no-underline hover:underline"
                >
                  {formatPhone(c.contact_phone)}
                </a>
              )}
            </span>
          ) : (
            <span className="text-faint">N/A</span>
          ),
      },
      {
        key: '_source',
        header: 'Source',
        pickerLabel: 'Source',
        icon: TagIcon,
        sortable: true,
        // Rows carry their own annotation: 'hubspot' when the data layer matched
        // this lead to a mirrored company (non-ambiguous), 'manual' for a
        // hand-added lead, else the imported database. Sort groups them.
        wrap: true,
        sortValue: (c) =>
          c._source === 'hubspot'
            ? 'hubspot'
            : c._source === 'manual'
              ? `manual ${c._addedByName || ''}`
              : 'imported',
        render: (c) => {
          if (c._source === 'hubspot') {
            const hs = c._hubspot || {}
            const basis = HS_MATCH_BASIS[hs.confidence]
            return (
              <span
                className="inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full bg-primary-tint px-2.5 py-[3px] text-[12px] font-semibold text-primary-dark"
                title={`Mirrored from your HubSpot CRM${hs.company?.name ? ` — ${hs.company.name}` : ''}${basis ? ` (matched on ${basis})` : ''}`}
              >
                {/* Brand dot — the app's primary treatment, so a HubSpot-sourced
                    row reads as connected data, not another import. */}
                <span className="h-[6px] w-[6px] flex-none rounded-full bg-primary" aria-hidden="true" />
                HubSpot
              </span>
            )
          }
          if (c._source === 'manual') {
            return (
              <span
                className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-tint px-2.5 py-[3px] text-[12px] font-semibold text-primary-dark"
                title={`Added manually by ${c._addedByName || c._addedBy || 'a teammate'}${c._addedAt ? ` on ${String(c._addedAt).slice(0, 10)}` : ''}`}
              >
                {c._addedByName || 'Added manually'}
              </span>
            )
          }
          // Imported (or unmatched). A near-miss the data layer flagged as
          // ambiguous stays "Imported", but gets a quiet hint so the rep knows
          // the CRM has look-alikes to settle — never dressed up as a match.
          return (
            <span className="inline-flex items-center gap-x-2">
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-panel px-2.5 py-[3px] text-[12px] font-semibold text-dim">
                Imported
              </span>
              {c._hubspot?.ambiguous && (
                <span
                  className="whitespace-nowrap text-[11.5px] font-medium text-faint"
                  title="Several HubSpot companies matched this lead equally well — open the lead to settle which one in HubSpot."
                >
                  multiple matches
                </span>
              )}
            </span>
          )
        },
      },
      {
        // HubSpot company owner — opt-in. The data layer already prefers the
        // HubSpot value over any owner on the imported row, so this is the CRM's.
        key: 'hs_owner',
        header: 'Owner',
        pickerLabel: 'Owner (HubSpot)',
        icon: UserIcon,
        sortable: true,
        defaultHidden: true,
        maxWidth: 200,
        sortValue: (c) => c._hubspot?.company?.owner_name || '',
        render: (c) => c._hubspot?.company?.owner_name || <span className="text-faint">N/A</span>,
      },
      {
        // Open deals from the HubSpot rollup: count + open amount. Absent/zero
        // reads as "N/A" (the mirror OMITS the field rather than writing a 0), so
        // an empty CRM never masquerades as "0 deals" of real data.
        key: 'hs_deals',
        header: 'Deals',
        pickerLabel: 'Open deals (HubSpot)',
        icon: HashIcon,
        sortable: true,
        align: 'center',
        defaultHidden: true,
        sortValue: (c) => Number(c._hubspot?.rollup?.open_deals) || 0,
        render: (c) => {
          const rollup = c._hubspot?.rollup || {}
          const open = Number(rollup.open_deals) || 0
          if (!open) return <span className="text-faint">N/A</span>
          const amount = formatAmount(rollup.open_deal_amount)
          return (
            <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
              <span className="tabular-nums text-ink">{open}</span>
              {amount && <span className="tabular-nums text-[12px] text-dim">{amount}</span>}
            </span>
          )
        },
      },
      {
        // OUTREACH — the one column that answers "have we talked to them?".
        // Read from the mirror's own inbound/outbound counts, so a reply is a
        // reply because HubSpot holds the inbound email, never because this app
        // assumed one. An unmatched company reads "N/A": no CRM record is not the
        // same as no contact, and the cell must not imply it.
        key: 'hs_outreach',
        header: 'Outreach',
        pickerLabel: 'Outreach (HubSpot)',
        icon: MailIcon,
        sortable: true,
        defaultHidden: true,
        // Replied first, then reached out, then untouched, then unknown — the
        // order a rep triages in.
        sortValue: (c) => {
          const o = outreachOf(c, sentMail)
          const rank = { replied: 3, sent: 2, none: 1, unknown: 0 }[o.state] || 0
          return rank * 1000 + Math.min(o.in * 10 + o.out, 999)
        },
        render: (c) => {
          const o = outreachOf(c, sentMail)
          if (o.state === 'unknown') {
            return (
              <span className="text-faint" title="This lead has no matched HubSpot record, so there is nothing to read — not the same as never contacted.">
                N/A
              </span>
            )
          }
          if (o.state === 'none') return <span className="text-faint">Not contacted</span>
          if (o.state === 'replied') {
            return (
              <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
                <span className="rounded-full bg-[#f0fdf4] px-[7px] py-px text-[11.5px] font-semibold text-[#15803d] dark:bg-[rgba(21,128,61,0.18)] dark:text-[#4ade80]">
                  Replied
                </span>
                <span className="text-[12px] tabular-nums text-dim">
                  {o.out ? `${o.out} sent · ` : ''}{o.in} in
                </span>
              </span>
            )
          }
          return (
            <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
              <span className="rounded-full bg-panel px-[7px] py-px text-[11.5px] font-semibold text-dim">
                Reached out
              </span>
              <span className="text-[12px] tabular-nums text-dim">{o.out} sent</span>
            </span>
          )
        },
      },
      {
        // HubSpot engagement rollup: when the company was last active + how many
        // emails are logged. Both come from the mirror and are omitted when
        // there's nothing, so the cell collapses to "N/A" rather than a fake 0.
        key: 'hs_activity',
        header: 'Activity',
        pickerLabel: 'Activity (HubSpot)',
        icon: MailIcon,
        sortable: true,
        defaultHidden: true,
        wrap: true,
        maxWidth: 220,
        sortValue: (c) => {
          const d = hubspotDate(c._hubspot?.rollup?.last_activity_at)
          return d ? d.getTime() : 0
        },
        render: (c) => {
          const rollup = c._hubspot?.rollup || {}
          const emails = Number(rollup.email_count) || 0
          const rel = relativeAge(rollup.last_activity_at)
          if (!rel && !emails) return <span className="text-faint">N/A</span>
          return (
            <span className="inline-flex items-center gap-x-2">
              {rel ? (
                <span className="whitespace-nowrap text-ink">{rel}</span>
              ) : (
                <span className="text-faint">N/A</span>
              )}
              {emails > 0 && (
                <span
                  className="whitespace-nowrap rounded-[6px] border border-line bg-panel px-[7px] py-[2px] text-[11.5px] text-dim"
                  title={`${emails} email engagement${emails === 1 ? '' : 's'} logged in HubSpot`}
                >
                  {emails} email{emails === 1 ? '' : 's'}
                </span>
              )}
            </span>
          )
        },
      },
    ],
    [enriched, enriching, category, calib],
  )

  // ── PRODUCT USAGE COLUMNS (every custom property the mirror still pulls) ───
  // The 13 contact customs (aggregated per company by the read lambda) plus
  // the 1 company-level custom the lambda copies onto the same by_company
  // record, so the directory is the one place every MIRRORED HubSpot field can
  // be read. It used to be 25 + 3: the owner dropped 14 columns (lead source /
  // status / offer / employment / LinkedIn, and the whole Zoom webinar and
  // recording set) and asked that we stop pulling the properties behind them
  // too, so those fields are no longer on the wire at all — there is nothing
  // here to render them from, which is why they are gone rather than hidden.
  // ALL of them default-visible: the ask was the data ON the page — the table
  // scrolls horizontally and the column organizer is where a rep trims it back
  // down — so nothing here carries defaultHidden any more. The reading order
  // is deliberate: activity dates → commercial standing → volume → lifecycle →
  // the company record's own last demo, so the row answers "are they using it /
  // are they paying / how much / who are they / when did we last show them"
  // left to right.
  // Appended AFTER every existing column, joined per row through the match the
  // data layer already made (row._hubspot.companyId → usage.by_company) — see
  // lib/leadUsage.js for the join and the unread≠empty rules. Every missing
  // value is a greyed "N/A" whose tooltip says WHICH silence it is (still loading /
  // could not read / no CRM match / no value held) — never a 0, never a 1970
  // date. Only rendered when the caller passes `usage` at all (see the prop).
  //
  // SEVEN KEYS CHANGED ON PURPOSE (hs_last_floorplan → hs_last_floorplan_analysis
  // and friends). Those columns used to ship defaultHidden, so saved column
  // prefs record them in knownKeys as deliberately-hidden; merely dropping
  // defaultHidden would surface them only for browsers with NO saved prefs.
  // A new key is DataTable's mechanism for "this column is new — show it at
  // its declared position" — for every browser whose saved prefs carry
  // knownKeys, which is prefs saved since that field shipped. LEGACY prefs
  // (saved before knownKeys existed) cannot tell "new" from "deliberately
  // hidden", so DataTable surfaces only alwaysShow columns there and those
  // browsers keep NOT seeing the renamed columns until the user saves once or
  // turns them on in the organizer — a known, accepted gap, not a guarantee
  // this memo makes. The old keys rot harmlessly in old prefs blobs, and no
  // dragged widths are lost that anyone could have set (the columns were
  // hidden).
  const usageColumns = useMemo(() => {
    if (usage === undefined) return []
    const aggOf = (c) => usageForLead(c, usage)
    const dash = (c) => (
      <span className="text-faint" title={usageDashReason(c, usage)}>
        N/A
      </span>
    )
    // Every usage header carries its field's aggregation rule as a tooltip
    // (the header becomes a node; pickerLabel keeps the organizer's plain
    // name — DataTable's columnLabel falls back to it). The rule sentence is
    // the LAMBDA's own where possible: route_users ships
    // definitions.by_company_fields built from the same tuples its aggregate
    // builder iterates, and usageFieldRule quotes it verbatim so the tooltip
    // cannot drift from the code that computed the value. The RULE_* strings
    // below are fallbacks for a payload that hasn't landed yet (or predates
    // the field definitions) — and because the fallback window is exactly the
    // window where nothing on the wire can correct them (the initial render
    // before the /users fetch resolves, a failed read, an old payload), each
    // sentence was checked 2026-08-28 against route_users'
    // USERS_COMPANY_FIELD_DEFINITIONS (the terraform repo's hubspot handler),
    // and each column helper takes a per-column fallbackRule so a field whose
    // lambda rule differs from the helper's usual one (Onboarded is EARLIEST,
    // not newest — the only such field left now that Lead imported and the
    // Zoom columns are gone) states ITS rule, not the helper's: even
    // mid-deploy, no tooltip here claims a rule the lambda does not implement.
    const head = (label, rule) => <span title={rule}>{label}</span>
    const ruleOf = (prop, fallback) => usageFieldRule(usage, prop, fallback)
    // How the lambda rolls each shape up, in the words the tooltips use.
    const RULE_MAX =
      "Most recent value across this company's contacts — the company is as recent as its most recent user."
    // Onboarded rolls up the OPPOSITE way: the claim is "on board since", and
    // the newest value would re-date the company every time a new contact is
    // added. Mirrors the lambda's _USERS_RULE_EARLIEST
    // (USERS_COMPANY_EARLIEST_FIELDS). Onboarded is the only column left that
    // uses it — Lead imported was the other one — and the sentence is left in
    // the lambda's own wording, "first imported" and all, because a tooltip
    // reworded here would no longer match the rule that computed the value.
    const RULE_EARLIEST =
      "Earliest value across this company's contacts — the claim is \"on board since / first imported\", and the newest value would re-date the company every time a new contact is added."
    const RULE_SUM =
      "Summed across this company's contacts; a contact without the property adds nothing (absent is not zero)."
    const RULE_COMPANY = "The HubSpot company record's own value — not a contact rollup."
    // A "last …" cell: relative age with the absolute timestamp in the title.
    // Only for datetimes in the past — relativeAge clamps the future to "just
    // now", so future dates (trial ends) render the absolute day instead.
    const lastDateCol = (key, label, prop, { pickerLabel, fallbackRule = RULE_MAX } = {}) => {
      const rule = ruleOf(prop, fallbackRule)
      return {
        key,
        header: head(label, rule),
        pickerLabel: pickerLabel || label,
        icon: CalendarIcon,
        sortable: true,
        sortValue: (c) => usageEpoch(aggOf(c)?.[prop]),
        render: (c) => {
          const rel = relativeAge(aggOf(c)?.[prop])
          if (!rel) return dash(c)
          const stamp = formatDayTime(aggOf(c)?.[prop])
          return (
            <span className="whitespace-nowrap text-ink" title={`${stamp} — ${rule}`}>
              {rel}
            </span>
          )
        },
      }
    }
    const countCol = (key, label, prop, { pickerLabel } = {}) => {
      const rule = ruleOf(prop, RULE_SUM)
      return {
        key,
        header: head(label, rule),
        pickerLabel: pickerLabel || label,
        icon: HashIcon,
        sortable: true,
        align: 'center',
        sortValue: (c) => usageNumber(aggOf(c)?.[prop]),
        render: (c) => {
          const n = usageNumber(aggOf(c)?.[prop])
          // A held 0 is a fact ("no projects yet") and renders as 0; only an
          // ABSENT value falls to the dash.
          if (n === '') return dash(c)
          return (
            <span className="tabular-nums text-ink" title={rule}>
              {n.toLocaleString()}
            </span>
          )
        },
      }
    }
    return [
      // ── activity: how recently the product was actually touched ────────────
      lastDateCol('hs_last_login', 'Last login', 'last_platform_login'),
      lastDateCol('hs_last_software_access', 'Last software access', 'last_software_access'),
      lastDateCol('hs_last_software_download', 'Last download', 'last_software_download'),
      lastDateCol('hs_last_elevation', 'Last elevation analysis', 'last_elevation_analysis'),
      lastDateCol('hs_last_floorplan_analysis', 'Last floorplan analysis', 'last_floorplan_analysis'),
      lastDateCol('hs_last_complete_analysis', 'Last complete analysis', 'last_complete_analysis'),
      // ── commercial: where the account stands ───────────────────────────────
      {
        // Where the subscription stands, as a pill a rep can triage on sight.
        // Sorted by the raw end timestamp (longest-expired first under asc),
        // never by the label.
        key: 'hs_subscription',
        header: head(
          'Subscription',
          `${ruleOf('subscription_end', RULE_MAX)} The pill turns amber within ${SUBSCRIPTION_WARN_DAYS} days of the end date, red once it has passed.`,
        ),
        pickerLabel: 'Subscription',
        icon: CalendarIcon,
        sortable: true,
        sortValue: (c) => usageEpoch(aggOf(c)?.subscription_end),
        render: (c) => {
          const sub = subscriptionStatus(aggOf(c)?.subscription_end)
          if (!sub) return dash(c)
          const title = `Subscription end on file: ${formatDay(aggOf(c)?.subscription_end)}`
          if (sub.state === 'expired') {
            return (
              <span title={title}>
                <StatusPill tone="danger">
                  {sub.days ? `Expired ${sub.days}d ago` : 'Expired today'}
                </StatusPill>
              </span>
            )
          }
          if (sub.state === 'ending') {
            return (
              <span title={title}>
                <StatusPill tone="warning">
                  {sub.days ? `Ends in ${sub.days}d` : 'Ends today'}
                </StatusPill>
              </span>
            )
          }
          return (
            <span title={title}>
              <StatusPill tone="success">Active</StatusPill>
            </span>
          )
        },
      },
      {
        // Trial end is usually in the FUTURE, so it renders as the absolute
        // day — relativeAge would clamp it to "just now" and lie.
        key: 'hs_trial_end_day',
        header: head('Trial ends', ruleOf('trial_ends', RULE_MAX)),
        pickerLabel: 'Trial ends',
        icon: CalendarIcon,
        sortable: true,
        sortValue: (c) => usageEpoch(aggOf(c)?.trial_ends),
        render: (c) => {
          const day = formatDay(aggOf(c)?.trial_ends)
          if (!day) return dash(c)
          return <span className="whitespace-nowrap text-ink">{day}</span>
        },
      },
      {
        // True if ANY contact on the company has a payment method attached
        // (the aggregate's own rule). An explicit false is a held fact and
        // renders "None"; an absent value is the usual dash.
        key: 'hs_payment_on_file',
        header: head(
          'Payment method',
          ruleOf(
            'payment_method_attached',
            "On file when ANY of this company's contacts has a payment method attached; None only when every contact carrying the property says false.",
          ),
        ),
        pickerLabel: 'Payment method',
        icon: TagIcon,
        sortable: true,
        sortValue: (c) => {
          const b = usageBool(aggOf(c)?.payment_method_attached)
          return b == null ? '' : b ? 1 : 0
        },
        render: (c) => {
          const b = usageBool(aggOf(c)?.payment_method_attached)
          if (b == null) return dash(c)
          return b ? (
            <span className="text-ink">On file</span>
          ) : (
            <span className="text-dim">None</span>
          )
        },
      },
      // ── volume: how much of the product they hold ──────────────────────────
      countCol('hs_projects', 'Projects', 'project_count'),
      countCol('hs_blueprint_count', 'Blueprints', 'blueprint_count'),
      {
        // How many product users the aggregate covers — contacts on this
        // company carrying at least one usage property.
        key: 'hs_users',
        header: head(
          'Users',
          ruleOf(
            'users',
            'How many mirrored HubSpot contacts carrying at least one product-usage property belong to this company.',
          ),
        ),
        pickerLabel: 'Users (product)',
        icon: UserIcon,
        sortable: true,
        align: 'center',
        sortValue: (c) => usersCountOf(aggOf(c)) ?? '',
        render: (c) => {
          const n = usersCountOf(aggOf(c))
          if (n == null) return dash(c)
          return <span className="tabular-nums text-ink">{n.toLocaleString()}</span>
        },
      },
      // ── lifecycle: how the account came to exist and where it stands ───────
      // EARLIEST, not newest — Onboarded answers "since when", and the
      // helper's usual newest fallback would state the rule inverted during
      // the fallback window (see RULE_EARLIEST).
      lastDateCol('hs_onboarded', 'Onboarded', 'onboarded_at', {
        pickerLabel: 'Onboard Timestamp',
        fallbackRule: RULE_EARLIEST,
      }),
      {
        // The version an account is running describes the account's state, so
        // it files with Onboarded rather than with the activity dates: those
        // say WHEN the product was touched, this says WHAT was running when it
        // was. (Stated because it could defensibly live either place.)
        // Key changed from hs_software_version — see the memo comment above.
        key: 'hs_running_version',
        header: head(
          'Software version',
          ruleOf(
            'software_version',
            'The version reported by the contact with the newest platform login — the version most recently seen running.',
          ),
        ),
        pickerLabel: 'Software version',
        icon: TagIcon,
        sortable: true,
        sortValue: (c) => String(aggOf(c)?.software_version || ''),
        render: (c) => {
          const v = String(aggOf(c)?.software_version || '').trim()
          if (!v) return dash(c)
          return <span className="whitespace-nowrap text-ink">{v}</span>
        },
      },
      // ── company-level custom ───────────────────────────────────────────────
      // Held ON the HubSpot company record itself, copied verbatim onto
      // by_company by the lambda — no contact aggregation, so the tooltip
      // states that instead of a rollup rule. Last demo is the only one left:
      // the two Zoom recording props beside it went with the webinar columns.
      {
        // STRING-typed in the portal (someone types this date by hand), so it
        // parses defensively: a string that unambiguously names a day (ISO
        // date-only rendered on the UTC clock so the day shown equals the day
        // typed, ISO datetime, 13+-digit epoch ms) → the usual "Aug 26, 2026"
        // day; anything else held → the raw string, because a value HubSpot
        // holds must never hide behind a dash or wear a wrong-but-confident
        // date ('March 2026' is NOT "Mar 1", and a 10-digit seconds stamp is
        // NOT January 1970); truly empty → the dash. The sort key is
        // usageStringDayEpoch — usageStringDay's exact twin, NOT the generic
        // usageEpoch — so raw-rendered strings sort among the absent rows
        // ('' epoch) instead of by a date the cell refuses to show: there is
        // no honest position for "sometime last spring" on a date axis.
        key: 'hs_last_demo',
        header: head('Last demo', ruleOf('last_demo__recording_date', RULE_COMPANY)),
        pickerLabel: 'Last Demo / Recording Date',
        icon: CalendarIcon,
        sortable: true,
        sortValue: (c) => usageStringDayEpoch(aggOf(c)?.last_demo__recording_date),
        render: (c) => {
          const label = usageStringDay(aggOf(c)?.last_demo__recording_date)
          if (!label) return dash(c)
          return (
            <span className="whitespace-nowrap text-ink" title={RULE_COMPANY}>
              {label}
            </span>
          )
        },
      },
    ]
  }, [usage])

  // Usage columns append AFTER every current column; a caller's extraColumns
  // (e.g. the audience list's "Added") stay last, as before.
  const allColumns = useMemo(
    () => (usageColumns.length ? [...columns, ...usageColumns] : columns),
    [columns, usageColumns],
  )

  if (companies.length === 0 && !loading) {
    return (
      <div className="rounded-[12px] border border-line bg-surface p-10 text-center text-dim">
        {emptyMessage}
      </div>
    )
  }

  return (
    <DataTable
      columns={extraColumns.length ? [...allColumns, ...extraColumns] : allColumns}
      rows={companies}
      rowKey={keyOf}
      defaultSort={defaultSort}
      singleSort
      onRowClick={(c) =>
        navigate(`/leads/company/${encodeURIComponent(keyOf(c))}`, {
          state: { company: c, category: catOf(c) },
        })
      }
      selectable
      selectedKeys={selectedKeys}
      onToggleRow={onToggleRow}
      onToggleAll={onToggleAll}
      pageSize={PAGE_SIZE}
      tableId={tableId || `leads-${category}`}
      columnsOpen={columnsOpen}
      onColumnsOpenChange={onColumnsOpenChange}
      emptyMessage={emptyMessage}
      loading={loading}
      loadingMessage={loadingMessage}
      fill
    />
  )
}
